import useInputHelper, { InputHelperData } from './inputHelper';
import { DefaultInputPropTypes } from './types';
import useFormValidation from '../useFormValidation';
import React, { useEffect, useMemo } from 'react';
// eslint-disable-next-line import/no-unresolved
import { CreatableAdditionalProps } from 'react-select/dist/declarations/src/useCreatable';

type DefaultOptionValue = StringOrNumber | { [Key: string]: unknown };

export type Option<TValue = DefaultOptionValue> = {
  isMessage?: boolean;
  value: TValue;
  label: string | React.ReactElement;
  disabled?: boolean;
  isSearchable?: boolean;
  [Key: string]: unknown;
};

export type NestedOption<TValue = DefaultOptionValue> = {
  label: string;
  options: Option<TValue>[];
};

export type DropdownOption<TValue = DefaultOptionValue> = Option<TValue> | NestedOption<TValue>;

export const isNestedOption = <TValue>(option: DropdownOption<TValue>): option is NestedOption<TValue> =>
  Array.isArray((option as NestedOption<TValue>).options);

export const getPrimitiveOptionValue = (value: Option['value']): StringOrNumber => {
  if (typeof value === 'object' && value) {
    const record = value as Record<string, unknown>;
    if (typeof record.id === 'string' || typeof record.id === 'number') {
      return record.id;
    }
    if (typeof record.value === 'string' || typeof record.value === 'number') {
      return record.value;
    }
    return JSON.stringify(record);
  }

  return value;
};

export type DefaultDropdownProps<
  OptionsType = DropdownOption[],
  ValueType extends string | number | string[] = string | number,
> = {
  options?: OptionsType;
  multiple?: boolean;
  placeholder?: string;
  helpText?: Nullable<string>;
  isLoading?: boolean;
  filterOption?: (option: { data: Option }, rawInput: string) => boolean;
  formatOptionLabel?: (
    data: Option,
    formatOptionLabelMeta: {
      context: 'menu' | 'value';
    },
  ) => React.ReactNode;
  styles?: Dictionary<any>;
  isCreatable?: boolean;
  isClearable?: boolean;
  filter?: Dictionary<any>;
  menuPlacement?: 'auto' | 'bottom' | 'top';
  placeholderOnly?: boolean;
  hasFilterByLabel?: boolean;
  getOptionValue?: (option: Option) => string;
} & DefaultInputPropTypes<ValueType> &
  CreatableAdditionalProps<any, any>;

interface DropdownData extends InputHelperData<string> {
  getSelectOptions: () => DropdownOption[] | undefined;
  isMultiple: () => boolean;
  getMultipleOnChangeCallback: () => (event: React.ChangeEvent<HTMLSelectElement>) => void;
}

const useDropdownHelper = (
  dropdownProps: DefaultDropdownProps<DropdownOption[], string | number | string[]>,
): DropdownData => {
  const inputHelper = useInputHelper(dropdownProps);

  /**
   * All options without filtering
   */
  const rawOptions = dropdownProps.options || [];

  const { getAllowedTransitions } = useFormValidation(dropdownProps.source);

  const isMultiple = () => {
    const { multiple = false } = dropdownProps;

    return multiple;
  };

  /*
   * Allowed transitions are calculated according to initial value.
   */
  // eslint-disable-next-line
  const initialValue = useMemo(() => inputHelper.getValue(), []);

  const getAllOptionValues = (option: DropdownOption): any[] => {
    if (isNestedOption(option)) {
      return option.options.map(opt => getPrimitiveOptionValue(opt.value));
    }
    return [getPrimitiveOptionValue(option.value)];
  };

  const getSelectOptions = () => {
    const allowedTransitions = getAllowedTransitions && getAllowedTransitions();
    const currentlyAllowedTransitions = allowedTransitions && allowedTransitions[initialValue as string];

    return rawOptions.filter(option => {
      if (!currentlyAllowedTransitions) {
        return true;
      }

      const optionValues = getAllOptionValues(option);

      return (
        optionValues.includes(initialValue) || optionValues.some(value => currentlyAllowedTransitions.includes(value))
      );
    });
  };

  useEffect(() => {
    if (!inputHelper.getPlaceholder()) {
      const filteredOptions = getSelectOptions();

      const findFirstOption = (options: DropdownOption[]): Option | null => {
        for (const option of options) {
          if (isNestedOption(option)) {
            if (option.options.length > 0) {
              return option.options[0];
            }
          } else {
            return option;
          }
        }
        return null;
      };

      const firstOption = findFirstOption(filteredOptions);
      const onChangeCallback = inputHelper.getRawOnChangeCallback();
      const isValueNull =
        inputHelper.getValue() === null || inputHelper.getValue() === undefined || inputHelper.getValue() === '';

      if (firstOption && onChangeCallback && isValueNull) {
        onChangeCallback({
          [inputHelper.getSource()]: firstOption.value,
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rawOptions.length]);

  /**
   * Returns onChange event handler for multiple select
   * (it's different from usual onChangeCallback because event.target.value returned
   * from js event contains value that was clicked last, not array of selected values).
   */
  const getMultipleOnChangeCallback = () => {
    const { getRawOnChangeCallback } = inputHelper;
    const source = inputHelper.getSource();
    const rawOnChangeCallback = getRawOnChangeCallback();

    return (event: React.ChangeEvent<HTMLSelectElement>) => {
      const { options } = event.target;
      const selectedValues = Array.from(options)
        .filter(option => option.selected)
        .map(option => option.value);

      if (rawOnChangeCallback) {
        rawOnChangeCallback({
          [source]: selectedValues,
        });
      }
    };
  };

  return {
    ...inputHelper,
    getSelectOptions,
    isMultiple,
    getMultipleOnChangeCallback,
  };
};

export default useDropdownHelper;
